import { useState, useEffect } from "react";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Paper,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import COLORS from "../styles/colors.json";
import { searchProductCodes } from "../services/api";

const MODAL_STYLE = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: COLORS.white,
    borderRadius: 2,
    padding: 3,
    minWidth: 1100,
    maxHeight: '80vh',
    overflow: 'hidden'
};

const SEARCH_BTN_SX = {
    minWidth: 100,
    borderRadius: 0,
    borderColor: '#1A2226',
    backgroundColor: '#1A2226',
    color: COLORS.white,
    '&:hover': { backgroundColor: COLORS.gray9 }
};

const HEADER_SX = { fontWeight: 600, color: COLORS.white, backgroundColor: COLORS.redDarker, whiteSpace: 'nowrap' };

const COLUMNS = [
    { key: 'ods_product_id', label: 'ODS PRODUCT ID' },
    { key: 'cf_sku', label: 'CF SKU' },
    { key: 'vol_sku', label: 'AW1 SKU' },
    { key: 'lsp_code', label: 'LSP CODE' },
    { key: 'oracle_sku', label: 'ORACLE SKU' },
    { key: 'part_number', label: 'VPN' },
    { key: 'description', label: 'DESCRIPTION' },
    { key: 'manufacturer_name', label: 'MANUFACTURER' }
];

export default function ProductSearchModal({
    open,
    onClose,
    initialQuery,
    preloadedResults,
    disambiguationMessage,
    onProductSelect,
    onSearchAnyway
}) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        if (!open) return;
        const prefill = initialQuery || '';
        setQuery(prefill);
        setError(null);
        setLoading(false);
        setPage(0);

        // If results were preloaded (disambiguation), display them immediately
        if (preloadedResults && preloadedResults.length > 0) {
            setResults(preloadedResults);
            setTotalCount(preloadedResults.length);
        } else {
            setResults([]);
            setTotalCount(0);
            if (prefill.trim() && prefill.trim().length < 4) {
                setError('Please enter at least 4 characters');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const doSearch = async (searchQuery) => {
        const q = (searchQuery || query).trim();
        if (q.length < 4) {
            setError('Please enter at least 4 characters');
            return;
        }

        setLoading(true);
        setError(null);
        setResults([]);
        setPage(0);

        try {
            const response = await searchProductCodes(q);
            if (response.count === 0) {
                setError('No products found');
            } else {
                setResults(response.results);
                setTotalCount(response.count);
            }
        } catch (err) {
            setError('Search failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (product) => {
        // Pass the vol_sku as the value for the Product Code field
        onProductSelect(product.vol_sku || product.cf_sku || product.ods_product_id);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') doSearch();
    };

    const handleQueryChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        if (error === 'Please enter at least 4 characters' && val.trim().length >= 4) {
            setError(null);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Box sx={MODAL_STYLE}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">Product Code Lookup</Typography>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </Box>

                {disambiguationMessage && (
                    <Typography
                        sx={{ backgroundColor: COLORS.lsYellow, padding: 1, mb: 2, textAlign: 'center', fontWeight: 600 }}
                    >
                        {disambiguationMessage}
                    </Typography>
                )}

                <Box mb={2}>
                    <Box display="flex" gap={1} alignItems="flex-start">
                        <TextField
                            value={query}
                            onChange={handleQueryChange}
                            onKeyDown={handleKeyDown}
                            label="Enter exact CF SKU, AW1 SKU, or VPN"
                            fullWidth
                            size="small"
                            autoFocus
                            error={!!error && error.includes('4 characters')}
                            helperText={error && error.includes('4 characters') ? error : ''}
                            inputProps={{ style: { textTransform: 'uppercase' } }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => doSearch()}
                            disabled={loading}
                            sx={SEARCH_BTN_SX}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'SEARCH'}
                        </Button>
                    </Box>
                </Box>

                {error && !error.includes('4 characters') && (
                    <Box mb={2}>
                        <Typography color="error">{error}</Typography>
                        {onSearchAnyway && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => onSearchAnyway(query)}
                                sx={{ mt: 1, borderRadius: 0, borderColor: COLORS.gray7, color: COLORS.gray7 }}
                            >
                                SEARCH ANYWAY WITH CURRENT VALUE
                            </Button>
                        )}
                    </Box>
                )}

                {loading && (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress />
                    </Box>
                )}

                {results.length > 0 && (
                    <>
                        {totalCount > results.length && (
                            <Typography
                                sx={{ backgroundColor: COLORS.lsYellow, padding: 1, mb: 1, textAlign: 'center', fontWeight: 600 }}
                            >
                                {`SHOWING FIRST ${results.length} RESULTS. PLEASE REFINE YOUR SEARCH.`}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {results.length} product{results.length > 1 ? 's' : ''} found — select the correct one:
                        </Typography>
                        <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {COLUMNS.map(col => (
                                            <TableCell key={col.key} sx={HEADER_SX}>
                                                {col.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((product) => (
                                        <TableRow
                                            key={product.ods_product_id}
                                            hover
                                            onClick={() => handleRowClick(product)}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            {COLUMNS.map(col => (
                                                <TableCell key={col.key}>
                                                    {product[col.key] || '—'}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            rowsPerPageOptions={[5, 10, 25]}
                            count={results.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(e, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </>
                )}
            </Box>
        </Modal>
    );
}
